import { createRef, memo, useCallback, useEffect, useMemo, useState } from "react";

import { BTPopover } from "@btnet/core-components/BTPopover";
import { cn } from "@btnet/utilities-core";

import { BdsButton, BdsIconPlus } from "@buildertrend/components";

import { BTSelectItem } from "types/apiResponse/apiResponse";
import { CostTypes, MarkedAs } from "types/enum";

import { getDefaultMarkupMarginForCostCostCodeCostType } from "utilities/lineItem/lineItemHelper";
import { recalculateAllFields } from "utilities/lineItem/markupUtility";

import { BTCheckbox } from "commonComponents/btWrappers/BTCheckbox/BTCheckbox";
import { BTFormItemAutomatic } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTIconEyeglasses } from "commonComponents/btWrappers/BTIcon";
import { BTSelect, type BTSelectMaxHeight } from "commonComponents/btWrappers/BTSelect/BTSelect";
import { CostItemMarkedAsDisplay } from "commonComponents/utilities/Grid/costItem/columnImplementations/columnMarkedAs";
import { type IGridCellRenderProps } from "commonComponents/utilities/Grid/Grid.types";
import {
    CostTypesValueDisplay,
    getCostTypesStringValue,
} from "commonComponents/utilities/LineItemContainer/cellImplementations/CostTypesValueDisplay";
import {
    type IPreviewLineItemProps,
    LineItemPreview,
} from "commonComponents/utilities/LineItemContainer/LineItemPreview";
import { GridLineItemColumnType } from "commonComponents/utilities/LineItemContainer/types/LineItem.enums";
import { type ILineItemColumn } from "commonComponents/utilities/LineItemContainer/types/LineItem.interfaces";
import {
    type IBaseLineItem,
    type ICostCodeCostTypesMarkup,
    type IMarkupLineItem,
} from "commonComponents/utilities/LineItemContainer/types/LineItem.types";
import { LineItemColumns } from "commonComponents/utilities/LineItemContainer/types/LineItem.types";

import { MarkupColumnTypes } from "handlers";

import { type ICostTypesMarkup } from "entity/job/Job.api.types";

import "./CellImplementations.less";

export interface ICostTypeCellProps extends IPreviewLineItemProps {
    costTypes: CostTypes[] | null;
    maxHeight?: BTSelectMaxHeight;
    ellipsis?: boolean;
    isChildOfSubCategory?: boolean;
    usePopoverPreview?: boolean;
    hasSingleSelectCostTypes?: boolean;
    isStackedLineItem?: boolean;
    markedAs?: MarkedAs;
    showMarkedAs?: boolean;
    // Cost Types can be hidden for printing
    hideCostTypes?: boolean;
    hasSelectionsReimagined?: boolean;
    isAllowanceLineItem?: boolean;
    showAddVarianceButton?: boolean;
    onAddVariance?: () => void;
}

export interface ICostTypeCellWithMarkupProps extends ICostTypeCellProps, IMarkupLineItem {
    id: number;
    markedAs: MarkedAs;
    costTypesMarkup: ICostTypesMarkup[];
    costCodeCostTypesMarkup?: ICostCodeCostTypesMarkup[];
}

interface ICostTypeSelectProps {
    id: string;
    "data-testid": string;
    hasSingleSelectCostTypes: boolean;
    costTypes?: CostTypes[] | null;
    onChange: (field: string, value: number[]) => void;
    onBlur: (field: string, isTouched: boolean) => void;
    readOnly?: boolean;
    autoFocus?: boolean;
    /** Opens the dropdown on mount. Used for cell-editor flows where one click should both enter edit mode and reveal options. */
    defaultOpen?: boolean;
    maxHeight?: BTSelectMaxHeight;
    maxTagCount?: number;
    maxTagPlaceholder?: (omittedValues: unknown[]) => React.ReactNode;
    className?: string;
}

export const CostTypeSelect: React.FC<ICostTypeSelectProps> = ({
    className,
    id,
    "data-testid": dataTestId,
    hasSingleSelectCostTypes,
    costTypes,
    onChange,
    onBlur,
    readOnly,
    autoFocus,
    defaultOpen,
    maxHeight,
    maxTagCount,
    maxTagPlaceholder,
}) => {
    const value = useMemo(() => {
        if (hasSingleSelectCostTypes && (!costTypes || costTypes.length === 0)) {
            return [CostTypes.None];
        }
        return costTypes ? Object.values(costTypes) : null;
    }, [costTypes, hasSingleSelectCostTypes]);

    const costTypeSelectOptions = useMemo(() => {
        const costTypes = Object.keys(CostTypes)
            .map((k: string) => CostTypes[k as keyof typeof CostTypes])
            .filter((v) => typeof v === "number")
            .filter((v) => {
                return v !== CostTypes.Bid && v !== CostTypes.Allowance;
            })
            .filter((v) => {
                if (!hasSingleSelectCostTypes) {
                    return v !== CostTypes.None;
                }
                return true;
            })
            .map((v: CostTypes) => new BTSelectItem({ title: CostTypes[v], id: v }));
        return costTypes;
    }, [hasSingleSelectCostTypes]);

    const handleChangeCostTypes = useCallback(
        (field: string, value: number | number[]) => {
            let costTypes: number[] = [];
            if (typeof value === "number") {
                costTypes = [value];
            } else {
                costTypes = value;
            }
            onChange(field, costTypes);
        },
        [onChange],
    );
    return (
        <BTSelect
            className={className}
            id={id}
            data-testid={dataTestId}
            treeData={costTypeSelectOptions}
            value={value}
            multiple={!hasSingleSelectCostTypes}
            onChange={handleChangeCostTypes}
            onBlur={onBlur}
            readOnly={readOnly}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={autoFocus}
            defaultOpen={defaultOpen}
            maxHeight={maxHeight}
            maxTagCount={maxTagCount}
            maxTagPlaceholder={maxTagPlaceholder}
        />
    );
};

const onBlur = () => {};

interface IMarkAsBidProps {
    id: string;
    "data-testid": string;
    markedAs?: MarkedAs;
    onChange: (field: string, value: number) => void;
    readOnly?: boolean;
    autoFocus?: boolean;
    maxHeight?: BTSelectMaxHeight;
    className?: string;
    isAllowanceLineItem?: boolean;
    hasSelectionsReimagined?: boolean;
}

export const markAsBidField = "markedAs";

export const MarkedAsSelect: React.FC<IMarkAsBidProps> = ({
    className,
    id,
    "data-testid": dataTestId,
    markedAs,
    onChange,
    readOnly,
    autoFocus,
    maxHeight,
    isAllowanceLineItem,
    hasSelectionsReimagined,
}) => {
    const MarkedAsSelectOptions = useMemo(() => {
        const markedAsValue = Object.keys(MarkedAs)
            .map((k: string) => MarkedAs[k as keyof typeof MarkedAs])
            .filter((v) => typeof v === "number" && v !== MarkedAs.LegacyBidAllowance)
            .filter((v) => {
                if (markedAs === MarkedAs.None || markedAs === MarkedAs.LegacyBidAllowance) {
                    // If none is selected or we're in a multiselect, remove None from the options
                    return v !== MarkedAs.None;
                }
                return v;
            })
            .map((v: MarkedAs) => new BTSelectItem({ title: MarkedAs[v], id: v }));
        return markedAsValue;
    }, [markedAs]);

    const showNone = !markedAs || markedAs < 0;
    let displayValue: MarkedAs | number[] | undefined = markedAs;
    if (markedAs === MarkedAs.LegacyBidAllowance) {
        displayValue = [1, 2];
    }

    const handleChangeMarkedAs = useCallback(
        (field: string, value: number | number[]) => {
            let markedAs;
            if (typeof value === "number") {
                markedAs = value;
            } else {
                markedAs = value[0];
            }
            onChange(field, markedAs);
        },
        [onChange],
    );

    // Clean-up the dropdown when the Allowance on Worksheet flag is turned on
    const markedAsSelectDropdown = (
        <BTSelect
            className={className}
            id={id}
            data-testid={dataTestId}
            treeData={MarkedAsSelectOptions}
            value={showNone ? "None" : displayValue}
            onChange={handleChangeMarkedAs}
            readOnly={readOnly}
            autoFocus={autoFocus}
            maxHeight={maxHeight}
            onBlur={() => {}}
            multiple={markedAs === MarkedAs.LegacyBidAllowance}
        />
    );

    const markAsBidCheckbox = (
        <BTCheckbox
            className={className}
            id={id}
            data-testid={dataTestId}
            checked={markedAs === MarkedAs.Bid || markedAs === MarkedAs.LegacyBidAllowance}
            onChange={(e) => onChange(id, e.target.checked ? MarkedAs.Bid : MarkedAs.None)}
            readOnly={readOnly}
        >
            Mark as Bid
        </BTCheckbox>
    );

    const removeAllowanceDropdownItem = hasSelectionsReimagined;

    // For allowance line items, always show the dropdown
    if (isAllowanceLineItem) {
        return markedAsSelectDropdown;
    }

    return removeAllowanceDropdownItem ? markAsBidCheckbox : markedAsSelectDropdown;
};

export const CostTypeCell: React.FunctionComponent<
    IGridCellRenderProps<ICostTypeCellProps | ICostTypeCellWithMarkupProps>
> = memo(function CostTypeCell(props) {
    const { cellValue, field, isInEdit, onChange, canEdit, autoFocus, onFocus } = props;
    const allowMultiple = !props.cellValue?.hasSingleSelectCostTypes;
    const costTypes = cellValue?.costTypes;
    const markedAs = cellValue?.markedAs;
    const showMarkedAs = cellValue?.showMarkedAs;
    const hideCostTypes = cellValue?.hideCostTypes;
    const hasSelectionsReimagined = cellValue?.hasSelectionsReimagined;
    const isAllowanceLineItem = cellValue?.isAllowanceLineItem;
    const showAddVarianceButton = cellValue?.showAddVarianceButton;
    const divRef = createRef<HTMLDivElement>();

    const [isOverFlow, setOverflow] = useState(false);

    useEffect(() => {
        if (divRef.current) {
            setOverflow(divRef.current.scrollHeight > divRef.current.clientHeight);
        }
    }, [divRef, cellValue, isOverFlow]);

    const onCostTypesChange = useCallback(
        (field: string, value: number[]) => {
            const lineItem = cellValue;
            if (
                lineItem &&
                "id" in lineItem &&
                lineItem.id <= 0 &&
                lineItem.costTypesMarkup.length > 0
            ) {
                const markupMargin = getDefaultMarkupMarginForCostCostCodeCostType(
                    lineItem.costTypesMarkup,
                    value,
                    lineItem.costCodeId,
                    lineItem.costCodeCostTypesMarkup,
                )[0];
                const markupPercent = markupMargin.markupPercent;
                const margin = markupMargin.margin ?? 0;
                const markupType = margin > 0 ? MarkupColumnTypes.Margin : lineItem.markupType;

                const calculatedFields = recalculateAllFields({
                    ...lineItem,
                    markupType,
                    markupPercent,
                    margin,
                });
                onChange(props.field.path, {
                    ...lineItem,
                    ...calculatedFields,
                    markupSource: markupMargin._markupSource,
                    costTypes: value,
                });
            } else {
                onChange(field, value);
            }
        },
        [cellValue, onChange, props.field.path],
    );

    if (cellValue === null || costTypes === null) {
        return <div tabIndex={0} onFocus={(e) => e.target.click()} />;
    }

    const canEditCostType = canEdit && !cellValue.isStackedLineItem; // readonly for stacked line items
    const markedAsPath = field.path + ".markedAs";

    let component = (
        <LineItemPreview
            fieldName="costTypes"
            addPreviewListener={cellValue.addPreviewListener}
            render={(preview) => (
                <CostTypeSelect
                    className={cn("top-zero", {
                        LineItemPreview: preview !== undefined,
                    })}
                    id={field.full}
                    data-testid={field.full}
                    costTypes={preview?.costTypes ?? costTypes}
                    hasSingleSelectCostTypes={!allowMultiple}
                    onChange={onCostTypesChange}
                    // because the BTSelect component triggers onblur once the component becomes visible, we will not pass in the onblur to prevent a rerender if the line item goes into edit mode
                    onBlur={onBlur}
                    readOnly={!isInEdit || !canEditCostType}
                    autoFocus={autoFocus}
                    maxHeight={cellValue?.maxHeight}
                />
            )}
        />
    );

    if (isInEdit) {
        return (
            <div
                className={cn("flex flex-col", {
                    "gap-10": showAddVarianceButton,
                })}
            >
                <div className="flex flex-row">
                    <BTFormItemAutomatic
                        id={field.full}
                        className={cn("LineItemCell", "bds-grow", {
                            ellipsis: cellValue?.ellipsis,
                        })}
                    >
                        {component}
                    </BTFormItemAutomatic>
                </div>

                {showMarkedAs && (
                    <div className="flex flex-row">
                        <BTFormItemAutomatic
                            id={markedAsPath}
                            className={cn("LineItemCell", "bds-grow", {
                                ellipsis: cellValue?.ellipsis,
                            })}
                            label={hasSelectionsReimagined ? null : "Mark As"}
                        >
                            <LineItemPreview
                                fieldName={markAsBidField}
                                addPreviewListener={cellValue.addPreviewListener}
                                render={(preview) => (
                                    <MarkedAsSelect
                                        className={cn({
                                            LineItemPreview: preview !== undefined,
                                        })}
                                        id={markedAsPath}
                                        data-testid={markedAsPath}
                                        markedAs={preview?.markedAs ?? markedAs}
                                        onChange={onChange}
                                        maxHeight={cellValue?.maxHeight}
                                        readOnly={!canEditCostType}
                                        isAllowanceLineItem={isAllowanceLineItem}
                                        hasSelectionsReimagined={hasSelectionsReimagined}
                                    />
                                )}
                            />
                        </BTFormItemAutomatic>
                    </div>
                )}
                {showAddVarianceButton && (
                    <div className="flex flex-row">
                        <BdsButton
                            className="bds-grow"
                            icon={<BdsIconPlus />}
                            text="Add variance"
                            testid={`${field.full}-add-variance`}
                            displayType="tertiary"
                            onClick={cellValue.onAddVariance}
                        />
                    </div>
                )}
            </div>
        );
    }

    const handleFocus = (event: React.FocusEvent<HTMLDivElement>) => {
        // Added "data-read-more-button" attribute to Show More text in Read More component
        // When clicking Show More in an editable Line Item, we don't want to focus the cell,
        // So the Read More component can expand instead
        if (!event.target.hasAttribute("data-read-more-button")) {
            onFocus?.();
        }
    };

    component = (
        <>
            <CostTypesValueDisplay
                data-testid={`${field.full}`}
                ref={divRef}
                costTypes={costTypes}
                className="costTypeArea"
                allowMultiple={allowMultiple}
            />
            {isOverFlow && cellValue.usePopoverPreview && (
                <BTPopover
                    content={getCostTypesStringValue(costTypes, allowMultiple)}
                    widthType="unrestricted"
                    overlayClassName="gridCellPopover"
                >
                    <BTIconEyeglasses
                        data-testid={`${field.full}-eyeglassicon`}
                        className="gridCellIcon margin-left-xss"
                        size={16}
                    />
                </BTPopover>
            )}
        </>
    );

    return (
        <div
            className={cn({
                lineItemEditableCell: canEditCostType,
                isChildOfSubCategory: cellValue?.isChildOfSubCategory,
            })}
            tabIndex={canEditCostType ? 0 : undefined}
            onFocus={handleFocus}
        >
            {!hideCostTypes && component}
            {showMarkedAs && (
                <div className={cn({ "margin-all-xss": !hideCostTypes })}>
                    <CostItemMarkedAsDisplay markedAs={markedAs} />
                </div>
            )}
        </div>
    );
});

export const costTypeCellDefaultConfig = <T extends IBaseLineItem>() =>
    ({
        id: LineItemColumns.CostTypes,
        name: "Cost type",
        jsonKey: "costTypes",
        width: 175.0,
        minWidth: 48,
        ellipsis: true,
        align: "left",
        isSortable: false,
        columnType: GridLineItemColumnType.costType,
        isFrozen: false,
        isHidden: false,
        Cell: CostTypeCell,
    }) as Partial<ILineItemColumn<T, ICostTypeCellProps>>;
